import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Checkbox, Icon, Loader, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import keycloak from '@/keycloak';
import useTranslate from '@/hooks/useTranslate';
import { getCurrentLanguageCode, getFullLanguageCode, getXlanguageCode } from '@/utils/i18n';
import Markdown from './Markdown';

interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
}

// ----- Web Speech API (mic dictation) -----
// The SpeechRecognition types are not part of lib.dom, so we declare the minimal shape we use.
// It is a browser-native API (Chrome/Edge/Safari) reached via the `webkit` prefix in most builds,
// which keeps the chat dependency-free.
interface SpeechRecognitionAlternativeLike {
    transcript: string;
}
interface SpeechRecognitionResultLike {
    readonly isFinal: boolean;
    readonly length: number;
    [index: number]: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionResultListLike {
    readonly length: number;
    [index: number]: SpeechRecognitionResultLike;
}
interface SpeechRecognitionEventLike {
    readonly resultIndex: number;
    readonly results: SpeechRecognitionResultListLike;
}
interface SpeechRecognitionErrorEventLike {
    readonly error: string;
}
interface SpeechRecognitionLike {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    start(): void;
    stop(): void;
    abort(): void;
    onresult: ((e: SpeechRecognitionEventLike) => void) | null;
    onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
    onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

const getSpeechRecognitionCtor = (): SpeechRecognitionCtor | null => {
    const w = window as unknown as {
        SpeechRecognition?: SpeechRecognitionCtor;
        webkitSpeechRecognition?: SpeechRecognitionCtor;
    };
    return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

const CHAT_URL = process.env.REACT_APP_MCP_CHAT_URL;

// The feature is gated by this DB-backed, per-company feature flag (default OFF).
// Resolved via GET /feature/{companyId}; see Full.tsx.
export const AI_ASSISTANT_FEATURE = 'ai-assistant';

// Site purple (matches the SVG circle and the app header gradient in components/Header.tsx).
const PURPLE = '#48237D';
const PURPLE_GRADIENT = 'linear-gradient(90deg, #48237D 0%, #8049CC 100%)';

const AssistantIcon: React.FC = () => (
    <svg width="56" height="56" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="25" cy="25" r="25" className="aia-circle-bg" fill="#48237d" />
        <path d="M25.0006 34.087C24.756 34.087 24.5183 34.0031 24.3226 33.8563C24.1268 33.7095 23.987 33.4998 23.9241 33.2692L22.596 28.1105C22.5541 27.9427 22.4632 27.7889 22.3374 27.6631C22.2116 27.5373 22.0578 27.4534 21.89 27.4045L16.7453 26.0763C16.5006 26.0064 16.2909 25.8666 16.1441 25.6709C15.9973 25.4682 15.9134 25.2305 15.9134 24.9929C15.9134 24.7552 15.9903 24.5175 16.1371 24.3218C16.2839 24.1261 16.4866 23.9863 16.7243 23.9164L21.89 22.5813C22.0578 22.5393 22.2116 22.4484 22.3374 22.3226C22.4632 22.1968 22.5471 22.043 22.596 21.8753L23.9241 16.7305C23.994 16.4859 24.1338 16.2762 24.3296 16.1294C24.714 15.8358 25.2942 15.8358 25.6787 16.1294C25.8744 16.2762 26.0142 16.4859 26.0771 16.7165L27.4052 21.8753C27.4472 22.043 27.538 22.1968 27.6569 22.3226C27.7827 22.4484 27.9365 22.5323 28.1042 22.5813L33.249 23.9094C33.4936 23.9793 33.7033 24.1191 33.8501 24.3148C33.9969 24.5035 34.0808 24.7482 34.0808 24.9929C34.0808 25.2375 33.9969 25.4822 33.8501 25.6709C33.7033 25.8596 33.4936 26.0064 33.2629 26.0693L28.1042 27.3975C27.9365 27.4394 27.7827 27.5303 27.6569 27.6561C27.531 27.7819 27.4472 27.9357 27.3982 28.1035L26.0701 33.2482C26.0002 33.4929 25.8604 33.7026 25.6647 33.8494C25.469 33.9961 25.2313 34.08 24.9866 34.08L25.0006 34.087ZM18.1503 24.9998L22.2395 26.0554C22.6519 26.1602 23.0224 26.3769 23.323 26.6775C23.6236 26.9781 23.8403 27.3555 23.9451 27.7609L25.0006 31.8502L26.0561 27.7609C26.161 27.3485 26.3777 26.9781 26.6782 26.6775C26.9788 26.3769 27.3563 26.1602 27.7617 26.0554L31.8509 24.9998L27.7617 23.9443C27.3493 23.8395 26.9718 23.6228 26.6713 23.3222C26.3707 23.0216 26.154 22.6442 26.0491 22.2387L24.9936 18.1495L23.9381 22.2387C23.8333 22.6512 23.6166 23.0216 23.316 23.3222C23.0154 23.6228 22.638 23.8395 22.2255 23.9443L18.1433 24.9998H18.1503Z" fill="white" />
        <path d="M31.2917 21.8753C30.8933 21.8753 30.5648 21.5537 30.5648 21.1483V17.6532C30.5648 17.2548 30.8863 16.9263 31.2917 16.9263C31.6972 16.9263 32.0187 17.2478 32.0187 17.6532V21.1483C32.0187 21.5467 31.6972 21.8753 31.2917 21.8753Z" fill="white" />
        <path d="M33.0393 20.1278H29.5442C29.1458 20.1278 28.8172 19.8062 28.8172 19.4008C28.8172 18.9954 29.1388 18.6738 29.5442 18.6738H33.0393C33.4377 18.6738 33.7662 18.9954 33.7662 19.4008C33.7662 19.8062 33.4447 20.1278 33.0393 20.1278Z" fill="white" />
        <path d="M18.367 31.8153C18.0804 31.8153 17.8427 31.5776 17.8427 31.291V28.495C17.8427 28.2084 18.0804 27.9707 18.367 27.9707C18.6536 27.9707 18.8912 28.2084 18.8912 28.495V31.291C18.8912 31.5776 18.6536 31.8153 18.367 31.8153Z" fill="white" />
        <path d="M19.758 30.3613H16.962C16.6754 30.3613 16.4377 30.1236 16.4377 29.837C16.4377 29.5504 16.6754 29.3127 16.962 29.3127H19.758C20.0446 29.3127 20.2823 29.5504 20.2823 29.837C20.2823 30.1236 20.0446 30.3613 19.758 30.3613Z" fill="white" />
    </svg>
);

const Fab = styled.button`
    position: fixed;
    right: 24px;
    bottom: 24px;
    width: 56px;
    height: 56px;
    padding: 0;
    border-radius: 50%;
    border: none;
    background: transparent;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    z-index: 1100;
    line-height: 0;
    &:hover {
        filter: brightness(1.06);
    }
    svg {
        display: block;
        width: 56px;
        height: 56px;
    }
`;

const Panel = styled.div`
    position: fixed;
    right: 24px;
    bottom: 92px;
    width: 440px;
    max-width: calc(100vw - 48px);
    height: 600px;
    max-height: calc(100vh - 130px);
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.28);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 1100;
`;

const Header = styled.div`
    padding: 12px 16px;
    background: ${PURPLE_GRADIENT};
    color: #fff;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const HeaderControls = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

// Hands-free toggle rendered on the purple header — force the Semantic checkbox label/track to read
// against the dark gradient (its defaults are tuned for light backgrounds).
const HandsFreeToggle = styled.div`
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 500;

    &&& .ui.toggle.checkbox label {
        color: #fff;
        /* Clear Semantic's 3.5rem toggle track (+ small gap) so the label text
           sits beside the switch instead of overlapping it. */
        padding-left: 3.75rem;
    }
    &&& .ui.toggle.checkbox label:before {
        background: rgba(255, 255, 255, 0.35);
    }
    &&& .ui.toggle.checkbox input:checked ~ label:before {
        background: rgba(255, 255, 255, 0.9) !important;
    }
    &&& .ui.toggle.checkbox input:checked ~ label:after {
        /* Match Semantic's default "on" knob position so it lands at the track's right edge. */
        left: 2.15rem;
    }
`;

const Messages = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: var(--gray-50, #f7f7f7);
`;

const Bubble = styled.div<{ role: 'user' | 'assistant' }>`
    margin-bottom: 10px;
    padding: 8px 12px;
    border-radius: 12px;
    word-break: break-word;
    line-height: 1.4;
    ${({ role }) =>
        role === 'user'
            ? `max-width: 85%; white-space: pre-wrap; margin-left: auto; background: ${PURPLE}; color: #fff; border-bottom-right-radius: 2px;`
            : `max-width: 97%; margin-right: auto; background: #fff; border: 1px solid var(--gray-100, #e0e0e0); border-bottom-left-radius: 2px;`}
`;

const InputBar = styled.div`
    display: flex;
    gap: 8px;
    padding: 12px;
    border-top: 1px solid var(--gray-100, #e0e0e0);
    background: #fff;
`;

const TextInput = styled.textarea`
    flex: 1;
    resize: none;
    border: 1px solid var(--gray-200, #ccc);
    border-radius: 8px;
    padding: 8px 10px;
    font-family: inherit;
    font-size: 14px;
    max-height: 96px;
`;

const AiChat: React.FC = () => {
    const { translate } = useTranslate();
    const history = useHistory();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [listening, setListening] = useState(false);
    // Hands-free mode: the mic auto-sends when you stop talking and re-opens after each reply, so a
    // whole conversation can be held by voice with no clicks. Persisted per browser.
    const [handsFree, setHandsFree] = useState(
        () => window.localStorage.getItem('aiChat.handsFree') === 'true'
    );
    const endRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
    // Text already in the input when dictation started — the live transcript is appended to it so a
    // mid-message mic toggle doesn't wipe what the user typed.
    const baseInputRef = useRef('');
    const [speechSupported] = useState(() => getSpeechRecognitionCtor() !== null);

    // Mirror hands-free into a ref so the recognition callbacks (created once per session) read the
    // live value rather than a stale closure.
    const handsFreeRef = useRef(handsFree);
    handsFreeRef.current = handsFree;
    // Guards the auto-restart-after-reply effect so it fires once per assistant message, not on every
    // unrelated re-render (which would loop the mic).
    const lastListenedReplyRef = useRef(0);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading, open]);

    // Keep the textarea focused so the user can type straight away: when the panel opens, and again
    // after each send finishes (loading clears) while the panel is still open. Gating on !loading means
    // the textarea is no longer `disabled` by the time we focus it.
    useEffect(() => {
        if (open && !loading) inputRef.current?.focus();
    }, [open, loading]);

    const stopListening = () => {
        recognitionRef.current?.stop();
    };

    const startListening = () => {
        // Don't stack sessions — a recognizer is already running.
        if (recognitionRef.current) return;

        const Recognition = getSpeechRecognitionCtor();
        if (!Recognition) {
            setError(translate('aiChat.speechUnsupported') as string);
            return;
        }

        // Captured once for this session so the callbacks below behave consistently even if the
        // hands-free toggle changes mid-utterance.
        const handsFreeSession = handsFreeRef.current;
        const recognition = new Recognition();
        recognition.lang = getFullLanguageCode(getCurrentLanguageCode());
        // Dictation keeps one long session open; hands-free ends on a natural pause so we can auto-send.
        recognition.continuous = !handsFreeSession;
        recognition.interimResults = true;

        // Dictation appends to whatever was typed; a hands-free turn always starts a fresh utterance.
        baseInputRef.current = !handsFreeSession && input.trim() ? `${input.trimEnd()} ` : '';
        let transcript = '';

        recognition.onresult = (e) => {
            transcript = '';
            for (let i = 0; i < e.results.length; i++) {
                transcript += e.results[i][0].transcript;
            }
            setInput(baseInputRef.current + transcript);
        };
        recognition.onerror = (e) => {
            // `aborted`/`no-speech` are routine (user stopped, or a silent pause) — don't surface them.
            if (e.error !== 'aborted' && e.error !== 'no-speech') {
                setError(translate('aiChat.speechError') as string);
            }
        };
        recognition.onend = () => {
            setListening(false);
            recognitionRef.current = null;
            // Hands-free: a finished utterance with content is sent automatically; the
            // restart-after-reply effect re-opens the mic for the next turn.
            if (handsFreeSession) {
                const spoken = transcript.trim();
                if (spoken) {
                    setInput('');
                    void send(spoken);
                }
            }
        };

        recognitionRef.current = recognition;
        setError(null);
        setListening(true);
        try {
            recognition.start();
        } catch {
            // start() throws if called while a previous session is still tearing down — reset and let
            // the user retry rather than leaving a stuck "listening" state.
            recognitionRef.current = null;
            setListening(false);
        }
    };

    const toggleListening = () => {
        if (listening) stopListening();
        else startListening();
    };

    const toggleHandsFree = () => {
        const next = !handsFree;
        setHandsFree(next);
        handsFreeRef.current = next;
        window.localStorage.setItem('aiChat.handsFree', String(next));
        // Turning it on starts a turn immediately; turning it off ends any active session.
        if (next) {
            if (open) startListening();
        } else {
            stopListening();
        }
    };

    // Tear down any active recognition session when the component unmounts.
    useEffect(() => () => recognitionRef.current?.abort(), []);

    // Stop the mic when the panel closes; when it (re)opens in hands-free mode, start a fresh turn.
    useEffect(() => {
        if (!open) recognitionRef.current?.abort();
        else if (handsFreeRef.current) startListening();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Hands-free conversation loop: after each assistant reply, re-open the mic for the next turn.
    // The ref guard ensures this runs once per new reply, never looping on incidental re-renders.
    useEffect(() => {
        if (!handsFree || !open || loading) return;
        const last = messages[messages.length - 1];
        if (last?.role === 'assistant' && messages.length !== lastListenedReplyRef.current) {
            lastListenedReplyRef.current = messages.length;
            startListening();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, loading, handsFree, open]);

    // `override` is set when the user clicks a suggested option/command box; otherwise the textarea
    // value is sent. Clicking an option leaves whatever the user had typed in the input untouched.
    const send = async (override?: string) => {
        const text = (override ?? input).trim();
        if (!text || loading) return;

        // Sending ends dictation: the textarea is about to clear, so leaving the mic on would
        // otherwise re-append the already-sent transcript on the next result.
        stopListening();
        setError(null);
        const next: ChatMessage[] = [...messages, { role: 'user', content: text }];
        setMessages(next);
        if (override === undefined) setInput('');
        setLoading(true);

        try {
            if (!CHAT_URL) throw new Error(translate('aiChat.notConfigured') as string);

            const response = await fetch(`${CHAT_URL.replace(/\/$/, '')}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${keycloak.token}`,
                    // Forwarded down to the BokaMera API so tool results are localised to the UI language.
                    'x-language': getXlanguageCode(),
                },
                body: JSON.stringify({ messages: next }),
            });

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || `HTTP ${response.status}`);
            }

            const data = (await response.json()) as { reply: string };
            setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void send();
        }
    };

    return (
        <>
            <Fab onClick={() => setOpen((o) => !o)} title={translate('aiChat.title') as string}>
                <AssistantIcon />
            </Fab>

            {open && (
                <Panel>
                    <Header>
                        <span>{translate('aiChat.title')}</span>
                        <HeaderControls>
                            {speechSupported && (
                                <HandsFreeToggle>
                                    <Checkbox
                                        toggle
                                        checked={handsFree}
                                        onChange={toggleHandsFree}
                                        label={translate('aiChat.handsFree') as string}
                                        title={translate('aiChat.handsFreeHint') as string}
                                    />
                                </HandsFreeToggle>
                            )}
                            <Icon name="close" link onClick={() => setOpen(false)} />
                        </HeaderControls>
                    </Header>

                    <Messages>
                        {messages.length === 0 && (
                            <Bubble role="assistant">{translate('aiChat.greeting')}</Bubble>
                        )}
                        {messages.map((m, i) => (
                            <Bubble key={i} role={m.role}>
                                {m.role === 'assistant' ? (
                                    <Markdown
                                        content={m.content}
                                        onAction={(v) => void send(v)}
                                        onNavigate={(href) => history.push(href)}
                                    />
                                ) : (
                                    m.content
                                )}
                            </Bubble>
                        ))}
                        {loading && (
                            <Bubble role="assistant">
                                <Loader active inline size="tiny" />{' '}
                                {translate('aiChat.thinking')}
                            </Bubble>
                        )}
                        {error && <Message negative size="tiny" content={error} />}
                        <div ref={endRef} />
                    </Messages>

                    <InputBar>
                        <TextInput
                            ref={inputRef}
                            rows={1}
                            value={input}
                            placeholder={
                                (listening
                                    ? translate('aiChat.listening')
                                    : translate('aiChat.placeholder')) as string
                            }
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            disabled={loading}
                        />
                        {speechSupported && (
                            <Button
                                icon={listening ? 'microphone slash' : 'microphone'}
                                title={
                                    (listening
                                        ? translate('aiChat.micStop')
                                        : translate('aiChat.micStart')) as string
                                }
                                aria-label={
                                    (listening
                                        ? translate('aiChat.micStop')
                                        : translate('aiChat.micStart')) as string
                                }
                                style={
                                    listening
                                        ? { backgroundColor: '#d8334a', color: '#fff' }
                                        : { backgroundColor: '#f0edf6', color: PURPLE }
                                }
                                onClick={toggleListening}
                                disabled={loading}
                            />
                        )}
                        <Button
                            icon="send"
                            style={{ backgroundColor: PURPLE, color: '#fff' }}
                            onClick={() => void send()}
                            disabled={loading || !input.trim()}
                        />
                    </InputBar>
                </Panel>
            )}
        </>
    );
};

export default AiChat;
